import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService, Toaster } from '@app/services';
import { LocalStorage } from 'ngx-webstorage';

export interface UserDeletionError {
  type: 'primary_contact' | 'organization_admin' | 'self_deletion' | 'general';
  message: string;
  user: any;
  clientCount?: number;
  clients?: any[];
}

@Component({
  selector: 'ngx-user-deletion-error-modal',
  templateUrl: './user-deletion-error-modal.component.html',
  styleUrls: ['./user-deletion-error-modal.component.scss']
})
export class UserDeletionErrorModalComponent implements OnInit {
  @LocalStorage() organizationId: string;
  @LocalStorage() role: string;

  @Input() error: UserDeletionError;

  loading = false;
  clients: any[] = [];
  organizationUsers: any[] = [];
  selectedReplacementUser: string = '';
  showReassignmentStep = false;

  constructor(
    public activeModal: NgbActiveModal,
    private $company: CompanyService,
    private $toaster: Toaster
  ) {}

  ngOnInit() {
    if (this.error?.type === 'primary_contact') {
      this.loadClientsAndUsers();
    }
  }

  async loadClientsAndUsers() {
    this.loading = true;
    try {
      // Load clients where this user is primary contact
      const clientsResponse = await this.$company.getClientsForUser(this.organizationId, this.error.user.id).toPromise();
      this.clients = clientsResponse || [];

      // Load other users in organization for reassignment
      const usersResponse = await this.$company.users(this.organizationId).toPromise();
      this.organizationUsers = (usersResponse || []).filter(user =>
        user.id !== this.error.user.id
      );
    } catch (error) {
      console.error('Error loading data:', error);
      this.$toaster.show('error', 'Error', 'Failed to load client and user data.');
    } finally {
      this.loading = false;
    }
  }

  getErrorTitle(): string {
    switch (this.error?.type) {
      case 'primary_contact':
        return 'Cannot Delete User - Primary Contact';
      case 'organization_admin':
        return 'Cannot Delete User - Administrator';
      case 'self_deletion':
        return 'Cannot Delete Your Own Account';
      default:
        return 'Cannot Delete User';
    }
  }

  getErrorIcon(): string {
    switch (this.error?.type) {
      case 'primary_contact':
        return 'fas fa-user-tie';
      case 'organization_admin':
        return 'fas fa-user-shield';
      case 'self_deletion':
        return 'fas fa-user-times';
      default:
        return 'fas fa-exclamation-triangle';
    }
  }

  getErrorDescription(): string {
    switch (this.error?.type) {
      case 'primary_contact':
        return `${this.error.user.first_name} ${this.error.user.last_name} cannot be deleted because they are the primary contact for ${this.error.clientCount || 'one or more'} client${(this.error.clientCount || 0) > 1 ? 's' : ''}.`;
      case 'organization_admin':
        return `${this.error.user.first_name} ${this.error.user.last_name} cannot be deleted because they are a Company Administrator. Only System Administrators can delete Company Administrators.`;
      case 'self_deletion':
        return 'You cannot delete your own user account. Please ask another administrator to delete your account if needed.';
      default:
        return this.error?.message || 'An error occurred while trying to delete this user.';
    }
  }

  showReassignmentOptions() {
    this.showReassignmentStep = true;
  }

  async reassignPrimaryContact() {
    if (!this.selectedReplacementUser) {
      this.$toaster.show('warning', 'Warning', 'Please select a replacement user.');
      return;
    }

    this.loading = true;
    try {
      // Reassign primary contact for all clients
      for (const client of this.clients) {
        await this.$company.updateClientPrimaryContact(
          this.organizationId,
          client.id,
          this.selectedReplacementUser
        ).toPromise();
      }

      this.$toaster.show('success', 'Success', 'Primary contact reassigned successfully. You can now delete the user.');
      this.activeModal.close('reassigned');
    } catch (error) {
      console.error('Error reassigning primary contact:', error);
      this.$toaster.show('error', 'Error', 'Failed to reassign primary contact. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  canReassign(): boolean {
    return this.error?.type === 'primary_contact' &&
           this.organizationUsers.length > 0 &&
           ['Lasgro Admin', 'Company Admin'].includes(this.role);
  }

  close() {
    this.activeModal.dismiss();
  }
}
